import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { MattersTypes } from '../../matters-types/interfaces/matterTypes.interface';
import {
  Area,
  Matter,
  MattersResponse,
  subjectNivelationAll,
  MattersByEducationLevel,
  JointAcademic,
} from '../interfaces/matters.interface';

import { GenerateCode } from '../interfaces/generateCode.interfaces';

import { setMattersList } from './mattersSlice';

export const mattersTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Matters', 'MattersAvailables', 'GenerateCodeSubject'],
});
// Define a service using a base URL and expected endpoints
export const mattersApi = mattersTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all matters types
     */
    getMatters: builder.query<
      MattersResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        typeSort?: string;
        sort?: string;
        educationLevelId?: number;
        off_description?: string;
        matterTypeId?: number;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          typeSort,
          sort,
          educationLevelId,
          off_description,
          matterTypeId,
        },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId!;
          const { data } = await mainApi.get<MattersResponse>('/subjects-view', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              type_sort: typeSort || 'desc',
              sort: sort || 'mat_name',
              search,
              ...(off_description && { off_description }),
              ...(matterTypeId && { type_matter_id: matterTypeId }),
              ...(educationLevelId && { education_level_id: educationLevelId }),
            },
          });

          dispatch(setMattersList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Matters', id } as const)),
              { type: 'Matters', id: 'LIST' },
            ]
          : [{ type: 'Matters', id: 'LIST' }],
    }),

    /**
     * @GET all matters to combo
     */
    getAvaliblesMatters: builder.query<
      Matter[],
      {
        search?: string;
        typeSort?: string;
        sort?: string;
        profileId?: number;
        periodId?: number;
        typeCalification?: string;
        educationLevelId?: string;
        off_description?: string;
      }
    >({
      queryFn: async (
        {
          profileId,
          periodId,
          typeCalification,
          educationLevelId,
          off_description,
          typeSort,
          sort,
          search,
        },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId!;

          const { data } = await mainApi.get<Matter[]>('/subjects', {
            params: {
              user_profile_id,
              type_sort: typeSort || 'desc',
              sort: sort || 'mat_name',
              search,
              ...(periodId && { period_id: periodId }),
              ...(typeCalification && { type_calification: typeCalification }),
              ...(educationLevelId && { education_level_id: educationLevelId }),
              ...(off_description && { offer_keyword: off_description }),
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'MattersAvailables', id: 'COMBO_MATTERS_LIST' }],
    }),

    /**
     * @GET all subjects by education LvL to combo
     */
    getAvaliblesMattersbyEducationLevel: builder.query<
      MattersByEducationLevel[],
      {
        profileId: number;
        educationLevelId: number;
        tm_acronym?: string;
        off_description?: string;
        careerId?: number;
        meshId?: number;
      }
    >({
      queryFn: async ({
        profileId,
        educationLevelId,
        tm_acronym,
        off_description,
        careerId,
        meshId,
      }) => {
        try {
          const { data } = await mainApi.get<MattersByEducationLevel[]>(
            `/education-levels/${educationLevelId}/subjects`,
            {
              params: {
                user_profile_id: profileId,
                tm_acronym,
                off_description,
                ...(careerId && { edu_lvl: careerId }),
                ...(meshId && { curriculum: meshId }),
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'MattersAvailables', id: 'COMBO_MATTERS_LIST' }],
    }),

    /**
     * @GET matter type by id
     */
    getMatter: builder.query<Matter, { profileId?: number; matterId: number }>({
      queryFn: async ({ profileId, matterId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId!;

          const { data } = await mainApi.get<Matter>(`/subjects/${matterId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Matters', profileId }],
    }),

    /**
     * @POST create matter type
     */
    addMatter: builder.mutation<Matter, { profileId: number; matterPayload: Partial<Matter> }>({
      queryFn: async ({ profileId, matterPayload }) => {
        try {
          const { data } = await mainApi.post<Matter>('/subjects', {
            user_profile_id: profileId,
            ...matterPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Matters', id: 'LIST' },
        { type: 'MattersAvailables', id: 'COMBO_MATTERS_LIST' },
      ],
    }),

    /**
     * @PUT update matter type
     */
    updateMatter: builder.mutation<
      Matter,
      { profileId: number; matterId: number; matterPayload: Partial<Matter> }
    >({
      queryFn: async ({ profileId, matterId, matterPayload }) => {
        try {
          const { data } = await mainApi.put<Matter>(`/subjects/${matterId}`, {
            user_profile_id: profileId,
            ...matterPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Matters', id: 'LIST' },
        { type: 'MattersAvailables', id: 'COMBO_MATTERS_LIST' },
      ],
    }),

    /**
     * @DELETE remove matter type
     */
    deleteMatter: builder.mutation<Matter, { profileId: number; matterId: number }>({
      queryFn: async ({ profileId, matterId }) => {
        try {
          const { data } = await mainApi.delete(`subjects/${matterId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Matters', id: 'LIST' },
        { type: 'MattersAvailables', id: 'COMBO_MATTERS_LIST' },
      ],
    }),

    /**
     * @GET all matters types
     */
    getAllMattersTypes: builder.query<MattersTypes[], { profileId: number; offerId?: string }>({
      queryFn: async ({ profileId, offerId }) => {
        try {
          const { data } = await mainApi.get<MattersTypes[]>('/type-matters', {
            params: {
              user_profile_id: profileId,
              ...(offerId && { offer_id: offerId }),
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET all area
     */
    getAvailablesArea: builder.query<Area[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Area[]>('/areas', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Subjects nivelation availables to combo box
     */
    getAvailablesSubjectsNivelation: builder.query<
      subjectNivelationAll[],
      { profileId: number; groupId?: number }
    >({
      queryFn: async ({ profileId, groupId }) => {
        try {
          const { data } = await mainApi.get<subjectNivelationAll[]>('/subjects', {
            params: {
              user_profile_id: profileId,
              ...(groupId && { area_group_id: groupId }),
              nivelation: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET all code to subject
     */
    getGenerateCodeSubject: builder.query<GenerateCode, { areaId: number }>({
      queryFn: async ({ areaId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const { data } = await mainApi.get<GenerateCode>(`/subjects/areas/${areaId}/codes`, {
            params: { user_profile_id: currentProfile?.profileId! },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'GenerateCodeSubject', id: 'COMBO' }],
    }),

    getJointAcademics: builder.query<JointAcademic[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<JointAcademic[]>('/joint-academics', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMattersQuery,
  useGetAvaliblesMattersQuery,
  useLazyGetAvaliblesMattersQuery,
  useAddMatterMutation,
  useDeleteMatterMutation,
  useGetMatterQuery,
  useUpdateMatterMutation,
  useGetAllMattersTypesQuery,
  useGetAvailablesAreaQuery,
  useGetAvailablesSubjectsNivelationQuery,
  useGetAvaliblesMattersbyEducationLevelQuery,
  useLazyGetMatterQuery,
  useLazyGetMattersQuery,
  useGetGenerateCodeSubjectQuery,
  useGetJointAcademicsQuery,
} = mattersApi;
